import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { API_BASE } from "../../config";
import Footer from './footer';
import { useNavigate } from 'react-router-dom';

const HomePage = ({ userUID }) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userUID) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE}/api/users/${userUID}`);
        setUserData(response.data);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError('Failed to load user data.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userUID]);

  if (loading) return <p className="text-gray-500 p-4">Loading...</p>;
  if (error) return <p className="text-red-500 p-4">{error}</p>;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 font-sans">
      <Helmet>
        <title>Home | MLMaths</title>
        <link rel="canonical" href="https://mlmaths.co.uk/home" />
        <meta name="description" content="Welcome to MLMaths — your pathway to GCSE Maths success with videos, quizzes, and progress tracking." />
      </Helmet>

      <main className="container mx-auto px-6 py-12 flex-grow">
        {/* Welcome Section */}
        <section className="mb-12 text-center">
          <div className="bg-white shadow-md rounded-lg p-6 mx-auto max-w-4xl">
            <h2 className="text-4xl font-extrabold text-teal-600 mb-4">
              Welcome {userData ? userData.first_name : 'User'}!
            </h2>
            <p className="text-gray-700 text-lg">
              Your pathway to GCSE Maths success starts here. Choose a level or explore topics to begin.
            </p>
          </div>
        </section>

        {/* Level Selection Cards */}
        <section className="mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {/* Foundation Level Card */}
            <div
              className="bg-white rounded-2xl shadow-xl p-6 cursor-pointer hover:shadow-2xl transition-all border-t-4 border-teal-500"
              onClick={() => navigate('/foundation')}
            >
              <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Foundation Level</h3>
              <p className="text-gray-600">Grades 1-5: Build a strong foundation in maths.</p>
            </div>

            {/* Higher Level Card */}
            <div
              className="bg-white rounded-2xl shadow-xl p-6 cursor-pointer hover:shadow-2xl transition-all border-t-4 border-orange-500"
              onClick={() => navigate('/higher')}
            >
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Higher Level</h3>
              <p className="text-gray-600">Grades 6-9: Master more advanced topics.</p>
            </div>

            {/* Formulas Card */}
            <div
              className="bg-white rounded-2xl shadow-xl p-6 cursor-pointer hover:shadow-2xl transition-all border-t-4 border-purple-400"
              onClick={() => navigate('/formula')}
            >
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Formulas</h3>
              <p className="text-gray-600">Quickly access all the essential formulas.</p>
            </div>

            {/* Exam Past Papers Card */}
            <div
              className="bg-white rounded-2xl shadow-xl p-6 cursor-pointer hover:shadow-2xl transition-all border-t-4 border-green-500"
              onClick={() => navigate('/examboard')}
            >
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Exam Past Papers</h3>
              <p className="text-gray-600">Access and practice past exam papers.</p>
            </div>
          </div>
        </section>

        {/* Topics Section */}
        <section className="mb-12">
          <h2 className="text-3xl text-teal-600 font-semibold mb-6 text-center">Explore Topics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {/* Numbers Card */}
            <div
              className="bg-white rounded-2xl shadow-xl p-6 cursor-pointer hover:shadow-2xl transition-all border-t-4 border-blue-500"
              onClick={() => navigate('/number')}
            >
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Numbers</h3>
              <p className="text-gray-600">Master number operations and properties.</p>
            </div>

            {/* Algebra Card */}
            <div
              className="bg-white rounded-2xl shadow-xl p-6 cursor-pointer hover:shadow-2xl transition-all border-t-4 border-blue-500"
              onClick={() => navigate('/algebra')}
            >
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Algebra</h3>
              <p className="text-gray-600">Learn equations, expressions, and more.</p>
            </div>

            {/* Geometry Card */}
            <div
              className="bg-white rounded-2xl shadow-xl p-6 cursor-pointer hover:shadow-2xl transition-all border-t-4 border-blue-500"
              onClick={() => navigate('/geometry')}
            >
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243zm0-5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Geometry</h3>
              <p className="text-gray-600">Understand shapes, angles, and measurements.</p>
            </div>

            {/* Ratio, Proportion & Rate of Change Card */}
            <div
              className="bg-white rounded-2xl shadow-xl p-6 cursor-pointer hover:shadow-2xl transition-all border-t-4 border-blue-500"
              onClick={() => navigate('/ratio')}
            >
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Ratio, Proportion & Rate of Change</h3>
              <p className="text-gray-600">Master ratios, proportions, and rates.</p>
            </div>

            {/* Probability Card */}
            <div
              className="bg-white rounded-2xl shadow-xl p-6 cursor-pointer hover:shadow-2xl transition-all border-t-4 border-blue-500"
              onClick={() => navigate('/probability')}
            >
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Probability</h3>
              <p className="text-gray-600">Learn probability theory and applications.</p>
            </div>

            {/* Statistics Card */}
            <div
              className="bg-white rounded-2xl shadow-xl p-6 cursor-pointer hover:shadow-2xl transition-all border-t-4 border-blue-500"
              onClick={() => navigate('/statistics')}
            >
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Statistics</h3>
              <p className="text-gray-600">Master data handling and analysis.</p>
            </div>
          </div>
        </section>

        {/* Progress Section */}
        <section className="mb-12">
          <h2 className="text-3xl text-teal-600 font-semibold mb-6 text-center">Track Your Progress</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Completed Topics */}
            <div
              className="bg-white rounded-xl shadow-md p-6 text-center cursor-pointer hover:bg-gray-100 transition"
              onClick={() => navigate("/completedtopics")}
            >
              <div className="bg-teal-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-teal-600 mb-3">Completed Topics</h3>
              <p className="text-gray-700">See what you've mastered so far.</p>
            </div>

            {/* Quizzes Taken */}
            <div
              className="bg-white rounded-xl shadow-md p-6 text-center cursor-pointer hover:bg-gray-100 transition"
              onClick={() => navigate("/quizhistory")}
            >
              <div className="bg-teal-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-teal-600 mb-3">Quizzes Taken</h3>
              <p className="text-gray-700">Review your quiz scores and history.</p>
            </div>

            {/* Exam Practice */}
            <div
              className="bg-white rounded-xl shadow-md p-6 text-center cursor-pointer hover:bg-gray-100 transition"
              onClick={() => navigate("/examTracking")}
            >
              <div className="bg-teal-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-teal-600 mb-3">Exam Practice</h3>
              <p className="text-gray-700">Track your practice for exams.</p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;